import { useState } from 'react';
import { toast } from 'sonner';
import { useProperties } from '@/features/properties/queries';
import { apiErrorMessage } from '@/lib/api';
import { formatCents, formatIsoDate } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from 'cn';
import { LedgerTabs } from './LedgerTabs';
import { PatrimoineChart } from './PatrimoineChart';
import { useAddPropertyValuation, useConsolidatedPatrimoine, usePropertyPatrimoine } from './queries';

function StatCard({ title, value }: { title: string; value: string }) {
  return (
    <Card>
      <CardHeader><CardTitle className="text-sm font-normal text-muted-foreground">{title}</CardTitle></CardHeader>
      <CardContent className="text-2xl font-semibold">{value}</CardContent>
    </Card>
  );
}

function ConsolidatedView() {
  const patrimoine = useConsolidatedPatrimoine();

  if (patrimoine.isPending) return <p className="text-muted-foreground">Chargement…</p>;
  if (patrimoine.isError) return <p className="text-destructive">Impossible de charger le patrimoine.</p>;

  const data = patrimoine.data;
  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard title="Valeur estimée totale" value={formatCents(data.totalValueCents, 'EUR')} />
        <StatCard title="Capital restant dû total" value={formatCents(data.totalOutstandingCents, 'EUR')} />
        <StatCard title="Patrimoine net" value={formatCents(data.netCents, 'EUR')} />
      </div>
      <p className="text-xs text-muted-foreground">Au {formatIsoDate(data.asOf)}.</p>

      <Card className="gap-0 overflow-hidden py-0">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/60 hover:bg-muted/60">
            <TableHead className="px-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:px-2">
              Bien
            </TableHead>
            <TableHead className="hidden px-1.5 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:table-cell sm:px-2">
              Valeur estimée
            </TableHead>
            <TableHead className="hidden px-1.5 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:table-cell sm:px-2">
              Capital restant dû
            </TableHead>
            <TableHead className="px-1.5 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:px-2">
              Net
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.byProperty.map((property) => (
            <TableRow key={property.propertyId}>
              <TableCell className="max-w-28 truncate px-1.5 sm:max-w-none sm:px-2" title={property.propertyName}>
                {property.propertyName}
              </TableCell>
              <TableCell className="hidden px-1.5 text-right tabular-nums sm:table-cell sm:px-2">
                {property.valueCents === null ? 'Aucune estimation' : formatCents(property.valueCents, property.currency)}
              </TableCell>
              <TableCell className="hidden px-1.5 text-right tabular-nums sm:table-cell sm:px-2">
                {formatCents(property.outstandingCents, property.currency)}
              </TableCell>
              <TableCell
                className={cn(
                  'px-1.5 text-right tabular-nums sm:px-2',
                  property.netCents !== null && property.netCents < 0 ? 'text-destructive' : undefined,
                )}
              >
                {property.netCents === null ? '—' : formatCents(property.netCents, property.currency)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </Card>
    </div>
  );
}

function AddValuationForm({ propertyId }: { propertyId: number }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState('');
  const addValuation = useAddPropertyValuation(propertyId);

  function submit() {
    const estimatedValueCents = Math.round(Number(amount) * 100);
    if (!Number.isFinite(estimatedValueCents) || estimatedValueCents <= 0) {
      toast.error('Montant invalide');
      return;
    }
    addValuation.mutate(
      { valuationDate: date, estimatedValueCents, notes: null },
      {
        onSuccess: () => {
          toast.success('Estimation ajoutée');
          setAmount('');
        },
        onError: (error) => toast.error(apiErrorMessage(error, "Échec de l'ajout")),
      },
    );
  }

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="flex flex-col gap-1">
        <span className="text-xs text-muted-foreground">Date de l'estimation</span>
        <Input type="date" value={date} onChange={(event) => setDate(event.target.value)} className="w-40" />
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-xs text-muted-foreground">Valeur estimée (€)</span>
        <Input type="number" step="0.01" min="0" value={amount} onChange={(event) => setAmount(event.target.value)} className="w-40" />
      </div>
      <Button onClick={submit} disabled={addValuation.isPending || amount === ''}>Ajouter une estimation</Button>
    </div>
  );
}

function PropertyView({ propertyId }: { propertyId: number }) {
  const patrimoine = usePropertyPatrimoine(propertyId);

  return (
    <div className="flex flex-col gap-6">
      <AddValuationForm propertyId={propertyId} />

      {patrimoine.isPending && <p className="text-muted-foreground">Chargement…</p>}
      {patrimoine.isError && <p className="text-destructive">Impossible de charger le patrimoine.</p>}

      {patrimoine.data && (
        <PropertyPatrimoineDetails
          current={patrimoine.data.current}
          history={patrimoine.data.history}
          currency={patrimoine.data.currency}
        />
      )}
    </div>
  );
}

function PropertyPatrimoineDetails({
  current,
  history,
  currency,
}: {
  current: { valueCents: number; outstandingCents: number; netCents: number } | null;
  history: readonly { date: string; valueCents: number; outstandingCents: number; netCents: number }[];
  currency: string;
}) {
  return (
    <>
      {current === null ? (
        <p className="text-muted-foreground">Aucune estimation saisie pour ce bien.</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatCard title="Valeur estimée" value={formatCents(current.valueCents, currency)} />
          <StatCard title="Capital restant dû" value={formatCents(current.outstandingCents, currency)} />
          <StatCard title="Net" value={formatCents(current.netCents, currency)} />
        </div>
      )}

      {history.length > 1 && <PatrimoineChart points={history} currency={currency} />}

      {history.length > 0 && (
        <Card className="gap-0 overflow-hidden py-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/60 hover:bg-muted/60">
              <TableHead className="px-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:px-2">
                Date
              </TableHead>
              <TableHead className="hidden px-1.5 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:table-cell sm:px-2">
                Valeur estimée
              </TableHead>
              <TableHead className="hidden px-1.5 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:table-cell sm:px-2">
                Capital restant dû
              </TableHead>
              <TableHead className="px-1.5 text-right text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:px-2">
                Net
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {[...history].reverse().map((point) => (
              <TableRow key={point.date}>
                <TableCell className="px-1.5 text-muted-foreground sm:px-2">{formatIsoDate(point.date)}</TableCell>
                <TableCell className="hidden px-1.5 text-right tabular-nums sm:table-cell sm:px-2">
                  {formatCents(point.valueCents, currency)}
                </TableCell>
                <TableCell className="hidden px-1.5 text-right tabular-nums sm:table-cell sm:px-2">
                  {formatCents(point.outstandingCents, currency)}
                </TableCell>
                <TableCell
                  className={cn(
                    'px-1.5 text-right tabular-nums sm:px-2',
                    point.netCents < 0 ? 'text-destructive' : undefined,
                  )}
                >
                  {formatCents(point.netCents, currency)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </Card>
      )}
    </>
  );
}

export function PatrimoinePage() {
  const [mode, setMode] = useState<'consolidated' | 'property'>('consolidated');
  const [propertyId, setPropertyId] = useState<string>('');
  const properties = useProperties();

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-2xl font-semibold">Ledger</h1>
      <LedgerTabs />

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex rounded-md border border-input p-0.5">
          <button
            type="button"
            onClick={() => setMode('consolidated')}
            className={cn('rounded px-3 py-1.5 text-sm font-medium', mode === 'consolidated' ? 'bg-accent' : 'text-muted-foreground')}
          >
            Consolidé
          </button>
          <button
            type="button"
            onClick={() => setMode('property')}
            className={cn('rounded px-3 py-1.5 text-sm font-medium', mode === 'property' ? 'bg-accent' : 'text-muted-foreground')}
          >
            Par bien
          </button>
        </div>

        {mode === 'property' && (
          <Select value={propertyId} onValueChange={setPropertyId}>
            <SelectTrigger><SelectValue placeholder="Choisir un bien" /></SelectTrigger>
            <SelectContent>
              {properties.data?.map((property) => (
                <SelectItem key={property.id} value={String(property.id)}>{property.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {mode === 'consolidated' && <ConsolidatedView />}
      {mode === 'property' && propertyId !== '' && <PropertyView propertyId={Number(propertyId)} />}
      {mode === 'property' && propertyId === '' && <p className="text-muted-foreground">Choisissez un bien.</p>}
    </div>
  );
}
